// @givmo/app-admin — the email sign-up's EARLY refusal, before any account exists (NF-FIX21).
//
// Since NF-FIX21 the email sign-up proves the work email BEFORE it registers the company, so the
// company create's own refusals now arrive after the code — on the details screen, which can fix a
// name but not a domain. Two of them can never be fixed by editing the details, and before NF-FIX21
// both failed on the email/password screen with the account rolled back, leaving nothing behind:
//   - a personal-email domain (the backend's `DOMAIN_BLACKLIST`);
//   - with join-or-create OFF, a domain that already has a company (the create's legacy refusal).
// This read keeps them there: the email/password screen asks BEFORE it creates the Firebase user, and
// shows TODAY's words for each — the create path's 400 details, composed from the same lower-cased
// domain the create reads off the Firebase record ("Domain <domain> is not allowed", "Domain <domain>
// is already in use", `company_service.create_company`) — never the read's own text.
//
// ★ THE VERDICT KEYS ON THE TYPED `reason` CODE AND ON NOTHING ELSE — the charity portal's rule for the
// same read (`apps/charity/src/features/auth/personal-email-block.ts`, CPA-WEB54). The read refuses
// three ways with `available: false`; the third — an address already on a Givmo account — is not this
// check's business: the account creation's row bootstrap refuses it and rolls the account back with
// today's message, exactly as the company create used to.
//
// ★ WITH JOIN-OR-CREATE ON, AN EXISTING COMPANY'S DOMAIN IS NOT REFUSED HERE. The create's fork pivots
// to request-access for an ACTIVE company and refuses a deactivated one, and this read cannot tell the
// two apart. So the sign-up proceeds; after the code the create decides — the join pivot, or the
// details screen with the create's own message. The create stays authoritative either way: a domain
// registered between this read and the code is refused there too.
//
// ★ FAIL OPEN, DELIBERATELY. A transport failure, a 4xx, an unreadable body, or a backend that does not
// yet send `company_domain_registered` (the web lands first) answers "no early refusal", and the
// company create after the code refuses instead — the slower path, never a locked door.

import { sdk } from '@givmo/api-client';

/** The read's typed answer for a blacklisted (personal-email) domain (`user_service.check_admin_email`). */
export const PERSONAL_EMAIL_DOMAIN_REASON = 'personal_email_domain';
/** The read's typed answer for a domain that already has a company (`user_service.check_admin_email`). */
export const COMPANY_DOMAIN_REGISTERED_REASON = 'company_domain_registered';

/** The address as the wire and the Firebase account both spell it. */
function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Narrow an unknown value to a plain object. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/**
 * Read the availability body into the early refusal's message, or `null` to proceed. Pure, so the two
 * refusals and every fail-open shape are pinned without a transport.
 */
export function decodeEarlyRefusal(
  body: unknown,
  email: string,
  joinOrCreateEnabled: boolean,
): string | null {
  if (!isRecord(body) || body.available !== false) return null;
  const domain = normalizeEmail(email).split('@').pop() ?? '';
  if (domain.length === 0) return null;
  if (body.reason === PERSONAL_EMAIL_DOMAIN_REASON) return `Domain ${domain} is not allowed`;
  if (body.reason === COMPANY_DOMAIN_REGISTERED_REASON && !joinOrCreateEnabled) {
    return `Domain ${domain} is already in use`;
  }
  return null;
}

/**
 * Ask the backend whether `email` can only fail the company create in a way the details cannot fix.
 * Resolves to today's create message for that refusal, or `null` to proceed; never throws.
 */
export async function companyEmailEarlyRefusal(
  email: string,
  joinOrCreateEnabled: boolean,
): Promise<string | null> {
  try {
    const { data, error } = await sdk.adminCheckEmailAvailable({
      query: { email: normalizeEmail(email) },
    });
    if (error !== undefined && error !== null) return null;
    return decodeEarlyRefusal(data, email, joinOrCreateEnabled);
  } catch {
    return null;
  }
}
